import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

const PRIMARY = '#3B82F6';
const SECONDARY = '#10B981';
const ACCENT = '#F97316';
const BG_TOP = '#08090c';
const BG_MID = '#0B1120';
const BG_BOT = '#0F1419';

const TOTAL_FRAMES = 720;

const PHASE = {
  title: [0, 155] as const,
  weights: [145, 260] as const,
  grid: [250, 470] as const,
  radar: [460, 650] as const,
  rank: [640, 720] as const,
};

function phaseOpacity(
  frame: number,
  enterStart: number,
  enterEnd: number,
  exitStart: number,
  exitEnd: number,
): number {
  const fadeIn = interpolate(frame, [enterStart, enterEnd], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  if (exitStart >= exitEnd) {
    return fadeIn;
  }
  const fadeOut = interpolate(frame, [exitStart, exitEnd], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  return fadeIn * fadeOut;
}

const CRITERIA = [
  { id: 'cost', label: '成本', weight: 0.3 },
  { id: 'risk', label: '风险', weight: 0.25 },
  { id: 'time', label: '周期', weight: 0.2 },
  { id: 'fit', label: '战略契合', weight: 0.25 },
];

const OPTIONS = [
  { key: 'A', name: '方案 A', color: PRIMARY },
  { key: 'B', name: '方案 B', color: SECONDARY },
  { key: 'C', name: '方案 C', color: ACCENT },
];

const SCORES: Record<string, number[]> = {
  A: [4, 3, 5, 4],
  B: [3, 5, 3, 5],
  C: [5, 2, 4, 3],
};

function weightedTotal(key: string): number {
  const s = SCORES[key];
  return CRITERIA.reduce((acc, c, i) => acc + s[i] * c.weight, 0);
}

const WEIGHTED = OPTIONS.map((o) => ({
  ...o,
  total: weightedTotal(o.key),
}));

const RadarPanel: React.FC<{ frame: number; fps: number; progress: number }> = ({ frame, fps, progress }) => {
  const cx = 410;
  const cy = 380;
  const R = 270;
  const n = CRITERIA.length;
  const angles = CRITERIA.map((_, i) => (-Math.PI / 2 + (i * 2 * Math.PI) / n) as number);

  const axis = (i: number) => ({
    x: cx + R * Math.cos(angles[i]),
    y: cy + R * Math.sin(angles[i]),
  });

  const valuePoint = (optIdx: number, critIdx: number, t: number) => {
    const raw = SCORES[OPTIONS[optIdx].key][critIdx] / 5;
    const r = R * raw * t;
    return {
      x: cx + r * Math.cos(angles[critIdx]),
      y: cy + r * Math.sin(angles[critIdx]),
    };
  };

  const buildPath = (optIdx: number, t: number) =>
    CRITERIA.map((_, i) => {
      const p = valuePoint(optIdx, i, t);
      return `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`;
    }).join(' ') + ' Z';

  return (
    <svg width={820} height={760} viewBox="0 0 820 760">
      <defs>
        <radialGradient id="radarBg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={PRIMARY} stopOpacity={0.06} />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
      <rect width={820} height={760} fill="url(#radarBg)" />
      {[0.25, 0.5, 0.75, 1].map((lvl) => (
        <polygon
          key={lvl}
          points={angles
            .map((ang) => {
              const rr = R * lvl * progress;
              return `${cx + rr * Math.cos(ang)},${cy + rr * Math.sin(ang)}`;
            })
            .join(' ')}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={1}
        />
      ))}
      {angles.map((ang, i) => {
        const a = axis(i);
        return (
          <line
            key={CRITERIA[i].id}
            x1={cx}
            y1={cy}
            x2={interpolate(progress, [0, 1], [cx, a.x])}
            y2={interpolate(progress, [0, 1], [cy, a.y])}
            stroke="rgba(255,255,255,0.12)"
            strokeWidth={2}
          />
        );
      })}
      {CRITERIA.map((c, i) => {
        const a = axis(i);
        const ox = (a.x - cx) * 1.15 + cx;
        const oy = (a.y - cy) * 1.15 + cy;
        return (
          <text
            key={c.id}
            x={ox}
            y={oy}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="rgba(255,255,255,0.55)"
            fontSize={26}
            fontWeight={600}
          >
            {c.label}
          </text>
        );
      })}
      {OPTIONS.map((opt, oi) => {
        const delay = oi * 18;
        const t = spring({
          frame: frame - PHASE.radar[0] - 40 - delay,
          fps,
          config: { damping: 14, stiffness: 90 },
        });
        const polyProg = t * progress;
        return (
          <path
            key={opt.key}
            d={buildPath(oi, polyProg)}
            fill={`${opt.color}22`}
            stroke={opt.color}
            strokeWidth={3}
            strokeLinejoin="round"
          />
        );
      })}
      <circle cx={cx} cy={cy} r={5} fill="#fff" opacity={0.6} />
    </svg>
  );
};

const WeightBars: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const start = PHASE.weights[0] + 20;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28, maxWidth: 1100, margin: '0 auto' }}>
      {CRITERIA.map((c, i) => {
        const p = spring({
          frame: frame - start - i * 16,
          fps,
          config: { damping: 15, stiffness: 100 },
        });
        const wPct = c.weight * 100 * p;
        return (
          <div key={c.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontSize: 30, fontWeight: 700, color: '#fff' }}>{c.label}</span>
              <span style={{ fontSize: 28, fontWeight: 700, color: ACCENT }}>{(c.weight * 100).toFixed(0)}%</span>
            </div>
            <div
              style={{
                height: 24,
                borderRadius: 12,
                background: 'rgba(255,255,255,0.06)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${wPct}%`,
                  height: '100%',
                  borderRadius: 12,
                  background: `linear-gradient(90deg, ${PRIMARY}, ${SECONDARY})`,
                  boxShadow: `0 0 20px ${PRIMARY}44`,
                }}
              />
            </div>
          </div>
        );
      })}
      <p style={{ margin: '16px 0 0', fontSize: 26, color: 'rgba(255,255,255,0.45)' }}>
        权重之和为 100%，反映决策者在当前情境下的优先级
      </p>
    </div>
  );
};

const ScoreGrid: React.FC<{ frame: number; fps: number; fillProg: number }> = ({ frame, fps, fillProg }) => {
  const cellW = 200;
  const cellH = 88;
  const headerH = 56;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ display: 'flex', marginLeft: 220 }}>
        {OPTIONS.map((o) => (
          <div
            key={o.key}
            style={{
              width: cellW,
              textAlign: 'center',
              fontSize: 30,
              fontWeight: 800,
              color: o.color,
              paddingBottom: 14,
            }}
          >
            {o.name}
          </div>
        ))}
      </div>
      {CRITERIA.map((c, ri) => (
        <div key={c.id} style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              width: 220,
              fontSize: 30,
              fontWeight: 700,
              color: 'rgba(255,255,255,0.75)',
              textAlign: 'right',
              paddingRight: 20,
            }}
          >
            {c.label}
            <span style={{ fontSize: 24, color: ACCENT, marginLeft: 10 }}>{(c.weight * 100).toFixed(0)}%</span>
          </div>
          {OPTIONS.map((o, oi) => {
            const idx = ri * 3 + oi;
            const cellDelay = idx * 10;
            const s = spring({
              frame: frame - PHASE.grid[0] - 25 - cellDelay,
              fps,
              config: { damping: 16, stiffness: 110 },
            });
            const score = SCORES[o.key][ri];
            const show = s * fillProg;
            return (
              <div
                key={`${o.key}-${c.id}`}
                style={{
                  width: cellW,
                  height: cellH,
                  margin: 8,
                  borderRadius: 18,
                  border: `2px solid ${o.color}33`,
                  background: `linear-gradient(135deg, ${o.color}18, rgba(255,255,255,0.02))`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: show,
                  transform: `scale(${interpolate(show, [0, 1], [0.88, 1])})`,
                }}
              >
                <span style={{ fontSize: 42, fontWeight: 900, color: o.color }}>
                  {interpolate(show, [0.5, 1], [0, score], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }).toFixed(
                    0,
                  )}
                </span>
              </div>
            );
          })}
        </div>
      ))}
      <div style={{ display: 'flex', marginLeft: 220, marginTop: 18 }}>
        {OPTIONS.map((o) => {
          const total = weightedTotal(o.key);
          const anim = spring({
            frame: frame - PHASE.grid[0] - 140 - oiDelay(o.key),
            fps,
            config: { damping: 12, stiffness: 85 },
          });
          return (
            <div
              key={o.key}
              style={{
                width: cellW,
                textAlign: 'center',
                fontSize: 26,
                fontWeight: 700,
                color: SECONDARY,
                opacity: anim,
              }}
            >
              加权 ≈ {interpolate(anim * fillProg, [0, 1], [0, total], { extrapolateRight: 'clamp' }).toFixed(2)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

function oiDelay(k: string): number {
  if (k === 'A') return 0;
  if (k === 'B') return 8;
  return 16;
}

const RankingBars: React.FC<{ frame: number; fps: number }> = ({ frame, fps }) => {
  const sorted = [...WEIGHTED].sort((a, b) => b.total - a.total);
  const maxScore = Math.max(...sorted.map((x) => x.total));
  const start = PHASE.rank[0] + 15;
  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {sorted.map((opt, rank) => {
        const p = spring({
          frame: frame - start - rank * 20,
          fps,
          config: { damping: 13, stiffness: 95 },
        });
        const barW = (opt.total / maxScore) * 100 * p;
        const isWinner = rank === 0;
        return (
          <div
            key={opt.key}
            style={{
              marginBottom: 28,
              opacity: p,
              transform: `translateX(${interpolate(p, [0, 1], [-40, 0])}px)`,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 26, fontWeight: 800, color: opt.color }}>
                #{rank + 1} {opt.name}
                {isWinner && (
                  <span style={{ marginLeft: 14, fontSize: 18, color: ACCENT, fontWeight: 700 }}>推荐</span>
                )}
              </span>
              <span style={{ fontSize: 28, fontWeight: 900, color: '#fff' }}>{opt.total.toFixed(2)}</span>
            </div>
            <div
              style={{
                height: 22,
                borderRadius: 11,
                background: 'rgba(255,255,255,0.06)',
                overflow: 'hidden',
                border: isWinner ? `2px solid ${ACCENT}` : '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <div
                style={{
                  width: `${barW}%`,
                  height: '100%',
                  borderRadius: 9,
                  background: `linear-gradient(90deg, ${opt.color}, ${isWinner ? ACCENT : opt.color}aa)`,
                  boxShadow: isWinner ? `0 0 28px ${ACCENT}55` : 'none',
                }}
              />
            </div>
          </div>
        );
      })}
      <p style={{ textAlign: 'center', marginTop: 24, fontSize: 21, color: 'rgba(255,255,255,0.45)' }}>
        分数为 1–5 量表；加权总分 = Σ（准则权重 × 该项得分）
      </p>
    </div>
  );
};

export const DecisionMatrixAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleS = spring({ frame, fps, config: { damping: 16, stiffness: 100 } });

  const opTitle = phaseOpacity(frame, PHASE.title[0], PHASE.title[0] + 22, PHASE.title[1] - 25, PHASE.title[1]);
  const introCardS = spring({ frame: frame - 18, fps, config: { damping: 16, stiffness: 90 } });
  const opWeights = phaseOpacity(frame, PHASE.weights[0] + 8, PHASE.weights[0] + 28, PHASE.weights[1] - 22, PHASE.weights[1]);
  const opGrid = phaseOpacity(frame, PHASE.grid[0] + 8, PHASE.grid[0] + 28, PHASE.grid[1] - 22, PHASE.grid[1]);
  const opRadar = phaseOpacity(frame, PHASE.radar[0] + 8, PHASE.radar[0] + 28, PHASE.radar[1] - 22, PHASE.radar[1]);
  const opRank = phaseOpacity(frame, PHASE.rank[0] + 8, PHASE.rank[0] + 30, TOTAL_FRAMES, TOTAL_FRAMES);

  const gridFill = interpolate(frame, [PHASE.grid[0] + 40, PHASE.grid[0] + 180], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const radarProg = interpolate(frame, [PHASE.radar[0] + 20, PHASE.radar[0] + 120], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(172deg, ${BG_TOP} 0%, ${BG_MID} 48%, ${BG_BOT} 100%)`,
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: '#fff',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at 75% 20%, ${PRIMARY}14 0%, transparent 42%),
            radial-gradient(ellipse at 15% 80%, ${SECONDARY}12 0%, transparent 38%)`,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'absolute',
          left: 72,
          top: 60,
          opacity: opTitle * titleS,
          transform: `translateY(${interpolate(titleS, [0, 1], [48, 0])}px)`,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 64,
            fontWeight: 900,
            letterSpacing: -0.5,
            background: `linear-gradient(115deg, ${PRIMARY}, ${SECONDARY}, ${ACCENT})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          决策矩阵
        </h1>
        <p style={{ margin: '10px 0 0', fontSize: 30, fontWeight: 700, color: SECONDARY }}>科学决策的结构化工具</p>
        <p style={{ margin: '6px 0 0', fontSize: 26, color: 'rgba(255,255,255,0.4)' }}>
          Weighted Decision Matrix · 让偏好与证据同屏
        </p>
      </div>

      <div
        style={{
          position: 'absolute',
          left: 100,
          right: 100,
          top: 360,
          opacity: opTitle * introCardS,
          transform: `translateY(${interpolate(introCardS, [0, 1], [30, 0])}px)`,
        }}
      >
        <div
          style={{
            padding: '36px 44px',
            borderRadius: 26,
            background: 'linear-gradient(125deg, rgba(59,130,246,0.14) 0%, rgba(16,185,129,0.1) 100%)',
            border: `1px solid rgba(249,115,22,0.25)`,
          }}
        >
          <h2 style={{ margin: 0, fontSize: 40, fontWeight: 800, color: PRIMARY }}>它解决什么问题？</h2>
          <p style={{ margin: '18px 0 0', fontSize: 30, lineHeight: 1.6, color: 'rgba(255,255,255,0.88)' }}>
            当多个方案难以凭直觉比较时，决策矩阵把<strong style={{ color: ACCENT }}> 准则、权重、打分 </strong>
            显式化。每个单元格记录“某方案在某准则下表现如何”，再按权重汇总，减少遗漏与事后合理化。
          </p>
          <div style={{ display: 'flex', gap: 20, marginTop: 26, flexWrap: 'wrap' }}>
            {['准则清晰', '权重透明', '可复盘', '易对齐团队'].map((tag) => (
              <span
                key={tag}
                style={{
                  padding: '8px 20px',
                  borderRadius: 999,
                  background: `${SECONDARY}22`,
                  color: SECONDARY,
                  fontSize: 24,
                  fontWeight: 600,
                  border: `1px solid ${SECONDARY}44`,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          left: 60,
          right: 60,
          top: 180,
          opacity: opWeights,
        }}
      >
        <h2 style={{ textAlign: 'center', fontSize: 46, fontWeight: 800, color: ACCENT, marginBottom: 34 }}>
          第一步：定义准则并分配权重
        </h2>
        <WeightBars frame={frame} fps={fps} />
      </div>

      <div
        style={{
          position: 'absolute',
          left: 40,
          right: 40,
          top: 120,
          opacity: opGrid,
        }}
      >
        <h2 style={{ textAlign: 'center', fontSize: 46, fontWeight: 800, color: PRIMARY, marginBottom: 34 }}>
          第二步：为各方案打分（示意）
        </h2>
        <ScoreGrid frame={frame} fps={fps} fillProg={gridFill} />
      </div>

      <div
        style={{
          position: 'absolute',
          left: 40,
          top: 120,
          opacity: opRadar,
          display: 'flex',
          width: 'calc(100% - 80px)',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 50,
        }}
      >
        <RadarPanel frame={frame} fps={fps} progress={radarProg} />
        <div style={{ maxWidth: 620 }}>
          <h2 style={{ fontSize: 48, fontWeight: 800, color: SECONDARY, margin: '0 0 20px' }}>多准则雷达对比</h2>
          <p style={{ fontSize: 30, lineHeight: 1.65, color: 'rgba(255,255,255,0.78)', margin: 0 }}>
            雷达图把同一套准则上的得分画成封闭多边形，形状差异直观呈现“强项与短板”。可与矩阵表交叉验证：若某方案总分高但雷达严重偏科，需检查权重是否真实反映风险承受度。
          </p>
          <ul style={{ margin: '24px 0 0', paddingLeft: 24, fontSize: 26, color: 'rgba(255,255,255,0.55)', lineHeight: 1.8 }}>
            <li>蓝色、绿色、橙色分别对应方案 A / B / C</li>
            <li>顶点越远，表示该准则得分越高（已按 5 分制归一）</li>
            <li>团队讨论时可单独调整权重再看图形变化</li>
          </ul>
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          left: 100,
          right: 100,
          top: 170,
          opacity: opRank,
        }}
      >
        <h2 style={{ textAlign: 'center', fontSize: 40, fontWeight: 800, color: ACCENT, marginBottom: 32 }}>
          加权汇总 · 最终排序
        </h2>
        <RankingBars frame={frame} fps={fps} />
      </div>
    </AbsoluteFill>
  );
};
